import { Divider, Grid, MenuItem, Skeleton, Typography, Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { ReactElement, useState } from "react"

import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardDialog } from "../../../../../shared/components/dialogs/StandardDialog"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import {
    fetchOrderDetails,
    MeetingOrderAction,
    MeetingServiceQuery,
    OrderDetailsResponse,
} from "../../../../nonAdminUser/reports/home/meetingServiceApi"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { MeetingServiceOrder } from "../accountingTypes"

const useStyles = makeStyles()(() => ({
    menu: {
        "& .MuiPaper-root": {
            padding: 0,
            minWidth: "10rem",
            width: "max-content",
            "& li:last-child": {
                marginBottom: "0.5rem",
            },
        },
        "& .MuiList-root": {
            padding: 0,
        },
    },
    dialogPaper: {
        paddingBottom: "2rem",
    },
    updateModalContainer: {
        padding: "0 2rem 0rem 2rem",
    },
    deleteItem: {
        color: tailwindPalette.red[600],
    },
    skeletonCard: {
        height: "1.5rem",
        minWidth: "14rem",
        margin: ".5rem",
        borderRadius: ".625rem",
    },
    topComponentDivider: {
        marginBottom: "0.5rem",
    },
    hideEdge: { display: "none" },
    noActionsMenuItem: {
        padding: "0.5rem 1rem",
        whiteSpace: "nowrap",
    },
}))

const transformData = (data: OrderDetailsResponse) => (data?.orders.length ? data?.orders[0] : undefined)
type MenuActionKey = keyof MeetingOrderAction | "showReceipt"

type MenuItemConfig = {
    key: MenuActionKey
    element: ReactElement
    dividerBefore?: boolean
}

export type AdditionalEventMenuItem = {
    label: string
    key: string
    onClick: (id: number) => void
}

type AccountingOrderMenuProps = {
    handleMenuClose: () => void
    setShowReceiptModal: (value: MeetingServiceOrder) => void
    openCreditNoteDialog: (orderId: number) => void
    order: MeetingServiceOrder
}

export function AccountingOrderMenu({
    handleMenuClose,
    setShowReceiptModal,
    openCreditNoteDialog,
    order,
}: AccountingOrderMenuProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [showLockedDialog, setShowLockedDialog] = useState({
        open: false,
        body: "",
        title: "",
    })

    const { data: orderDetails, isLoading } = useApiQuery(fetchOrderDetails, {
        queryName: MeetingServiceQuery.ORDER_DETAILS,
        dependencies: {
            orderId: order.id,
        },
        select: transformData,
    })

    const orderAction = orderDetails?.orderAction

    const handleShowReceipt = () => setShowReceiptModal(order)
    const closeLockedDialog = () => {
        setShowLockedDialog({
            open: false,
            title: "",
            body: "",
        })
        handleMenuClose()
    }

    const onRefund = () => {
        if (orderAction?.creditMeetingService?.allow) {
            openCreditNoteDialog(order.id)
        } else {
            setShowLockedDialog({
                open: true,
                title:
                    orderAction?.creditMeetingService?.message?.title ||
                    l10n.getString("user-home-canteen-upcoming-orders-refund-order"),
                body:
                    orderAction?.creditMeetingService?.message?.body ||
                    l10n.getString("user-home-canteen-upcoming-orders-refund-order-restriction-message"),
            })
        }
    }

    const renderMenuItems = (orderAction?: MeetingOrderAction) => {
        if (!orderAction) return []
        return menuItemsConfig.reduce<React.ReactNode[]>(
            (acc, { key, element, dividerBefore }, index) => {
                const isShowReceipt = key === "showReceipt"

                if (isShowReceipt) {
                    acc.push(element)
                    return acc
                }

                const action = orderAction[key]

                if (!action) return acc
                if (dividerBefore && acc.length > 0) acc.push(<Divider key={`divider-${index}`} />)

                acc.push(element)
                return acc
            },
            [
                <LocalizedStrict id="user-home-canteen-upcoming-orders-receipt-item">
                    <MenuItem onClick={handleShowReceipt}>Receipt</MenuItem>
                </LocalizedStrict>,
            ],
        )
    }

    const noActionsAvailable = [
        <LocalizedStrict
            id="user-home-canteen-news-category-no-actions"
            vars={{
                orderId: order.id.toString(),
            }}
        >
            <Box className={classes.noActionsMenuItem}>No actions available for order #{order.id.toString()}</Box>
        </LocalizedStrict>,
    ]

    const menuItemsConfig: MenuItemConfig[] = [
        {
            key: "creditMeetingService",
            element: (
                <LocalizedStrict id="meeting-service-table-credit-note-button">
                    <MenuItem onClick={onRefund}>Credit note</MenuItem>
                </LocalizedStrict>
            ),
        },
    ]

    const loadingSkeletons = (
        <>
            <Skeleton variant="rectangular" className={classes.skeletonCard} />
        </>
    )

    const menuItems = isLoading ? loadingSkeletons : orderAction ? renderMenuItems(orderAction) : noActionsAvailable

    return (
        <>
            {menuItems}
            {showLockedDialog.open && (
                <StandardDialog
                    open={showLockedDialog.open}
                    onClose={closeLockedDialog}
                    maxWidthProp="sm"
                    dialogTitle={showLockedDialog.title}
                    slotProps={{ paper: { className: classes.dialogPaper } }} // Test on test env
                >
                    <Grid container direction="column" justifyContent="center" alignItems="center" gap="2rem">
                        <Typography variant="body1">{showLockedDialog.body}</Typography>
                        <LocalizedStrict id="floating-action-button">
                            <StandardButton fullWidth={false} onClick={closeLockedDialog}>
                                Close
                            </StandardButton>
                        </LocalizedStrict>
                    </Grid>
                </StandardDialog>
            )}
        </>
    )
}
